export interface VideoReadyInfo {
  ready: boolean;
  bufferedSeconds: number;
  readyState: number;
}

/**
 * Wraps the ceremony <video> element: starts loading immediately (as soon as
 * the lobby is reached, never waiting for mission complete), and reports
 * buffering progress so the server can track per-player readiness.
 */
export class VideoPreloader {
  constructor(private readonly video: HTMLVideoElement) {
    this.video.preload = "auto";
    this.video.playsInline = true;
    this.video.muted = false;
    this.video.controls = false;
    this.video.disablePictureInPicture = true;
    this.video.loop = false;
  }

  load(src: string): void {
    this.video.src = src;
    this.video.load();
  }

  bufferedSeconds(): number {
    const b = this.video.buffered;
    if (b.length === 0) return 0;
    return b.end(b.length - 1);
  }

  isReadyEnough(): boolean {
    // HAVE_FUTURE_DATA (3) or better means playback can start smoothly.
    return this.video.readyState >= 3;
  }

  getInfo(): VideoReadyInfo {
    return {
      ready: this.isReadyEnough(),
      bufferedSeconds: this.bufferedSeconds(),
      readyState: this.video.readyState,
    };
  }

  onReadyChange(cb: (info: VideoReadyInfo) => void): () => void {
    const handler = () => cb(this.getInfo());
    const events = ["progress", "loadeddata", "canplay", "canplaythrough", "loadedmetadata"];
    events.forEach((ev) => this.video.addEventListener(ev, handler));
    return () => events.forEach((ev) => this.video.removeEventListener(ev, handler));
  }

  /**
   * Must be called synchronously (or immediately after) inside a user gesture
   * handler (the ENTER MISSION click). Primes autoplay permission so the
   * ceremony video can start with sound later without being blocked.
   */
  async primeForAutoplay(): Promise<void> {
    try {
      const wasMuted = this.video.muted;
      this.video.muted = true;
      await this.video.play();
      this.video.pause();
      this.video.currentTime = 0;
      this.video.muted = wasMuted;
    } catch {
      // Autoplay priming can fail silently on some browsers; the real play()
      // call at ceremony time (also close to a user gesture chain) is the
      // one that matters most.
    }
  }
}
